import React, {useState} from 'react';
import {
  Modal,
  Form,
  TextField,
  FormLayout,
  Banner,
  TextStyle,
} from '@shopify/polaris';
import {WithI18nProps, withI18n} from '@shopify/react-i18n';
import FormState, {
  validators,
  validate,
  FormData,
} from '@shopify/react-form-state-next';
import {isValidEmail, Routes} from '../../utilities';
import {fetchJSON, notifyFetchErrors} from 'utilities/fetch';
import {PrimaryActionProps} from '@shopify/polaris/types/components/Page/components/Header/Header';

interface OwnProps {
  open: boolean;
  expertHandle: string;
  serviceHandle: string;
  businessName: string;
  onClose(): void;
}

type CombinedProps = OwnProps & WithI18nProps;

interface FormFields {
  name: string;
  email: string;
  message: string;
}

function DirectContactModal({
  open,
  businessName,
  expertHandle,
  serviceHandle,
  onClose,
  i18n,
}: CombinedProps) {
  const [success, setSuccess] = useState(false);

  const initialValues: FormFields = {
    name: '',
    email: '',
    message: '',
  };

  const fieldLables = {
    name: i18n.translate('Profile.DirectContactModal.labelName'),
    email: i18n.translate('Profile.DirectContactModal.labelEmail'),
    message: i18n.translate('Profile.DirectContactModal.labelMessage'),
  };

  const errorMessages = {
    name: i18n.translate('Profile.DirectContactModal.errorName'),
    email: i18n.translate('Profile.DirectContactModal.errorEmail'),
    message: i18n.translate('Profile.DirectContactModal.errorMessage'),
    emailInvalid: i18n.translate(
      'Profile.DirectContactModal.errorEmailInvalid'
    ),
  };

  const onSubmit = async ({fields}: FormData<FormFields>) => {
    const data = {
      name: fields.name.value,
      email: fields.email.value,
      message: fields.message.value,
      organizationHandle: expertHandle,
      serviceHandle,
    };

    try {
      const response = await fetchJSON(Routes.preCommerceLeadSubmitPath, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.errors) return setSuccess(true);

      return response;
    } catch (error) {
      return notifyFetchErrors('PreContactError', {error});
    }
  };

  return (
    <FormState
      validateOnSubmit
      initialValues={initialValues}
      onSubmit={onSubmit}
      validators={{
        name: validators.requiredString(errorMessages.name),
        email: [
          validators.requiredString(errorMessages.email),
          validate(isValidEmail, errorMessages.emailInvalid),
        ],
        message: validators.requiredString(errorMessages.message),
      }}
    >
      {({fields, submit, submitting}) => {
        const primaryAction: PrimaryActionProps = success
          ? {
              onAction: onClose,
              content: i18n.translate(
                'Profile.DirectContactModal.successActionPrimary'
              ),
            }
          : {
              onAction: submit,
              loading: submitting,
              content: i18n.translate(
                'Profile.DirectContactModal.actionPrimary'
              ),
            };

        const secondaryActions = success
          ? []
          : [
              {
                content: i18n.translate(
                  'Profile.DirectContactModal.actionSecondary'
                ),
                onAction: onClose,
              },
            ];

        return (
          <Modal
            open={open}
            onClose={onClose}
            title={i18n.translate('Profile.DirectContactModal.title', {
              businessName,
            })}
            primaryAction={primaryAction}
            secondaryActions={secondaryActions}
          >
            {success ? (
              <>
                <Modal.Section>
                  <Banner
                    status="success"
                    title={i18n.translate(
                      'Profile.DirectContactModal.successBannerTitle'
                    )}
                  >
                    {i18n.translate(
                      'Profile.DirectContactModal.successBannerText'
                    )}
                  </Banner>
                </Modal.Section>
                <Modal.Section>
                  {i18n.translate('Profile.DirectContactModal.successNote', {
                    email: (
                      <TextStyle variation="strong">
                        {fields.email.value}
                      </TextStyle>
                    ),
                  })}
                </Modal.Section>
              </>
            ) : (
              <Modal.Section>
                <Form onSubmit={submit}>
                  <FormLayout>
                    <TextField label={fieldLables.name} {...fields.name} />
                    <TextField
                      type="email"
                      label={fieldLables.email}
                      {...fields.email}
                    />
                    <TextField
                      label={fieldLables.message}
                      multiline={8}
                      placeholder={i18n.translate(
                        'Profile.DirectContactModal.placeholderMessage'
                      )}
                      {...fields.message}
                    />
                  </FormLayout>
                </Form>
              </Modal.Section>
            )}
          </Modal>
        );
      }}
    </FormState>
  );
}

export default withI18n()(DirectContactModal);
